import {isObservableArray} from "mobx";
import {getCookieFromString, regexQuote} from "./StringUtilities";
import URI from 'urijs';
import {getConfig} from "../_configs/AppConfig";

const {publicRuntimeConfig} = getConfig();

import {OrganizationTypesWithStaffBlock} from "../types/OrganizationTypeValues";


// This will return false when rendered on the server (because SchoolBlocks isn't rendered on the server in JS) and
// on client side is true/false only if the window.SchoolBlocksApp exists.
export function isSchoolBlocksApp(): boolean {
    return process.env.NEXT_PUBLIC_APP_CTX === "schoolblocks" || (typeof window !== "undefined" ? "SchoolBlocksApp" in window : false);
}

/*
main page - FLEX_GRID
my feed page - LIST_GRID (feed grid and search grid are almost the same but use different filters)
following page - LIST_GRID
groups/departments/sections pages - ALPHA_GRID
search page - LIST_GRID
staff page - ALPHA_GRID
schools page - LIST_GRID (very similar to collection grid but with different filters)
 */

export const gridTypesEnum = {
    LIST_GRID: "LIST_GRID",
    ALPHA_GRID: "ALPHA_GRID",
    FLEX_GRID: "FLEX_GRID",
}

export function subOrganizationTypesByParent(parentType: OrganizationType): Array<OrganizationType> {
    let allowedTypes: Array<OrganizationType> = [];
    switch (parentType) {
        case "district":
            allowedTypes.push("department");
            break;

        case "school":
            allowedTypes.push("group", "team");
            break;

        case "organization":
            allowedTypes.push("department", "group", "team");
            break;

        default:
            // no changes to the default
            break;
    }

    allowedTypes.push("section");
    return allowedTypes;
}

export function subOrganizationTypesForStaffBlock(parentType: OrganizationTypesWithGrids): Array<OrganizationTypesWithStaffBlock> {
    const allSubOrgs = subOrganizationTypesByParent(parentType);
    return allSubOrgs
        .filter((t: OrganizationType) =>
            OrganizationTypesWithStaffBlock.includes(t as OrganizationTypesWithStaffBlock)) as Array<OrganizationTypesWithStaffBlock>;
}

export function getDistrictUrl(organization) {
    if (organization.type === 'school' && organization.ancestor_tree && organization.parent_id !== "root") {
        const district = getDistrict(organization);
        return district.primary_hostname?.scheme + "://" + district.primary_hostname?.hostname;
    }
    return null;
}

// returns district, or if there is no parent district, returns current organization
export function getDistrict(organization) {
    let district;
    if (organization.type === 'school' && organization.ancestor_tree && organization.parent_id !== "root") {
        let org = organization;
        while (org.parent_id !== "root") {
            org = organization.ancestor_tree.find(a => a.id === org.parent_id);
        }
        district = org;
    } else {
        district = organization;
    }
    return district;
}

export function makeNounPossessive(noun: string): string | null {
    if (typeof noun !== "string") return noun;

    if (noun.toLowerCase()[noun.length - 1] === "s") {
        return noun + "'";
    }
    return noun + "'s";
}

export function getCdnUrl(url: string): string {
    if (/^http/.test(url) || !url) return url;
    return publicRuntimeConfig.MEDIA_CDN_URL + (url[0] === "/" ? "" : "/") + url;
}

export function isArray(arr: any): boolean {
    return Array.isArray(arr) || isObservableArray(arr);
}

export function isNotEmptyArray(arr: any): boolean {
    return (isArray(arr) || isObservableArray(arr)) && arr.length > 0;
}

const localUrlRegex = /^[/#]/i;
const absoluteUrlRegex = /^(http[s]?|webcal):\/\//;
const emailUrlRegex = /^mailto:.+@.+\..+/;
const javascriptRegex = /^javascript:/i;
const adminLinkRegex = new RegExp(/\?spview=/);
const worldWideWebRegex = /(?!:\/\/)(www\.)/;

export function isMissingProtocol(href: string): boolean {
    return !absoluteUrlRegex.test(href) && !javascriptRegex.test(href) && !localUrlRegex.test(href) && !emailUrlRegex.test(href);
}

function getCurrentHostnameRegexes(currentFullUrl) {
    const currentHostname = currentFullUrl ? URI(currentFullUrl).hostname() : "";

    // added (=){0} to make sure it doesn't capture when we stick the current URL in a URL parameter
    const currentHostnameRegex = new RegExp(regexQuote(currentHostname), 'i');

    // negative lookbehinds break mobile safari, so this is the best I can think of for now
    const currentHostnameInUrlParamsRegex = new RegExp(`\\?(.*)${regexQuote(currentHostname)}`, 'i');

    return {currentHostnameRegex, currentHostnameInUrlParamsRegex};
}

// denotes that we are currently on a page rendered with Next, and the link is to a page also rendered with Next
export function isInternalNextLink(href: string | object, currentFullUrl: string): boolean {
    const {currentHostnameInUrlParamsRegex} = getCurrentHostnameRegexes(currentFullUrl);

    try {
        return typeof href === 'object' || (
            (/^\//.test(href) || (
                    URI(href.replace(worldWideWebRegex, "")).hostname() === URI(currentFullUrl.replace(worldWideWebRegex, "")).hostname() &&
                    !currentHostnameInUrlParamsRegex.test(href)) ||
                adminLinkRegex.test(href)
            )
        )
    } catch (e) {
        return false
    }
}

export function isExternalLink(href: string, currentFullUrl: string): boolean {
    const {currentHostnameRegex, currentHostnameInUrlParamsRegex} = getCurrentHostnameRegexes(currentFullUrl);
    return absoluteUrlRegex.test(href) && (!currentHostnameRegex.test(href)) || currentHostnameInUrlParamsRegex.test(href)
}

interface IQueries {
    [key: string]: string
}

export function getQueries(fullUrl: string): IQueries {
    return URI.parseQuery(URI(fullUrl).query());
}

export function getSessionId(cookies) {
    return getCookieFromString("PHPSESSID", cookies);
}

export function renderGoogleLanguage(googleCode) {
    const event = new CustomEvent('last-async-block', {
        bubbles: true,
        detail: {
            langCode: googleCode,
        },
    });
    setTimeout(() => window.dispatchEvent(event), 1000);
}
